import "../../../shared/components/modal/BaseSmallModalFormComponent.css";
import "./GpsTrackerDescargaModal.css";
import { BaseCreateComponent } from "../../../shared/components/BaseCreateComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { GpsTrackerDiaEnviosResponse } from "../../../shared/response/hardware/GpsTrackerDiaEnviosResponse";
import { NotFoundError } from "../../../shared/services/api/error/NotFoundError";
import { downloadCsvTalCual } from "../../../shared/utils/csvUtils";
import { GpsTrackerService } from "../../services/hardware/GpsTrackerService";

/** `aaaa-mm-dd` → el día con su nombre («mié, 29 oct 2026»). Se arma en UTC para que la zona del navegador no corra el día. */
function fmtDiaEnvios(fecha: string): string {
  const [anio, mes, dia] = fecha.split("-").map(Number);
  return new Date(Date.UTC(anio, mes - 1, dia)).toLocaleDateString("es-CO", {
    timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

/**
 * Ventana chica para descargar el CSV de los envíos de un GPS en un día (2026-10-29, pedido del usuario: "descarga de
 * solo csv que llega, por fecha", un día a la vez). La abren el menú Hardware y la pestaña Dispositivos del cliente.
 *
 * Al abrirse pide los días en que llegaron envíos (`ListarDiasEnvios`) y muestra solo esos, con cuántos llegaron cada
 * día, para escoger uno (pedido del usuario: "necesito saber qué días tengo disponibles para seleccionar UN SOLO DÍA").
 * Es una lista de opciones y no el selector de fecha del navegador, porque ese no puede ocultar los días sin datos.
 *
 * Usa los estilos de la ventana estándar (`sx-dlg-*`) pero no `BaseSmallModalFormComponent`: esa exige un servicio de
 * guardado y una respuesta con `id` e `isEliminado`, que una descarga no tiene (decisión del usuario, opción B).
 */
export class GpsTrackerDescargaModal extends BaseCreateComponent<HTMLDivElement> {
  private _cuerpo!: HTMLDivElement;
  private _descargarBtn!: HTMLButtonElement;
  /** Día escogido (`aaaa-mm-dd`); `null` mientras no hay días para escoger. */
  private _fecha: string | null = null;
  /** Hay una descarga en curso: cambiar de día no vuelve a habilitar «Descargar». */
  private _descargando = false;
  /**
   * La ventana se está cerrando o ya se cerró: «Descargar» queda apagado y lo que llegue de una llamada en curso se
   * descarta, sin bajar el archivo ni mostrar avisos (revisión del 2026-10-29).
   */
  private _cerrada = false;

  /** @param _codigoInterno Número de inventario del GPS, que nombra su base de envíos. */
  constructor(private readonly _codigoInterno: number) {
    super();
  }

  /** Contenedor de la ventana: un `div`. */
  protected getTagName(): string { return "div"; }
  /** Id fijo del fondo de la ventana de descarga. */
  protected getElementId(): string { return "sxGpsDescargaOverlay"; }
  /** Clase del fondo de la ventana estándar (`sx-dlg-overlay`). */
  protected getCssClassList(): string[] { return ["sx-dlg-overlay"]; }

  /** Arma la ventana, conecta cerrar y la descarga, y pide los días con datos. */
  protected override afterInit(el: HTMLDivElement): void {
    el.innerHTML = `
      <div class="sx-dlg-card">
        <div class="sx-dlg-header">
          <span class="sx-dlg-title">Descargar CSV · GPS ${this._codigoInterno}</span>
          <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm sx-dlg-close-btn" type="button" aria-label="Cerrar">
            <i class="ph ph-x"></i>
          </button>
        </div>
        <div class="sx-dlg-body">
          <div class="cl-form-fields gd-cuerpo">
            <p class="gd-estado">Buscando los días con datos…</p>
          </div>
        </div>
        <div class="sx-dlg-footer">
          <button class="sx-btn sx-btn-ghost sx-dlg-cancel-btn" type="button">Cancelar</button>
          <button class="sx-btn sx-btn-primary sx-dlg-save-btn" type="button" disabled><i class="ph ph-download-simple"></i> Descargar</button>
        </div>
      </div>
    `;

    this._cuerpo = el.querySelector<HTMLDivElement>(".gd-cuerpo")!;
    this._descargarBtn = el.querySelector<HTMLButtonElement>(".sx-dlg-save-btn")!;

    el.querySelector<HTMLButtonElement>(".sx-dlg-cancel-btn")!.onclick = () => this.dispose();
    el.querySelector<HTMLButtonElement>(".sx-dlg-close-btn")!.onclick = () => this.dispose();
    el.addEventListener("click", (e) => { if (e.target === el) this.dispose(); });
    this._descargarBtn.onclick = () => void this._descargarAsync();
    void this._cargarDiasAsync();
  }

  /**
   * Cierra con la misma animación de salida que la ventana estándar. Antes marca la ventana como cerrada: durante la
   * animación «Descargar» ya no recibe clics, y lo que llegue después se descarta.
   */
  public override dispose(): void {
    this._cerrada = true;
    this._validar();
    this.getElement().classList.add("exit");
    setTimeout(() => super.dispose(), 180);
  }

  /**
   * Pide los días con envíos del GPS y los pinta. Si nunca envió datos (o ya no está en el inventario), lo dice en la
   * ventana con el texto del Server; otro error, además, sale en el aviso de errores. Todo error se escribe en consola.
   * Si la ventana se cerró mientras esperaba, descarta la respuesta y no muestra avisos.
   */
  private async _cargarDiasAsync(): Promise<void> {
    try {
      const dias = await GpsTrackerService.instance.listarDiasEnvios(this._codigoInterno);
      if (this._cerrada) {
        console.warn(`[GpsTrackerDescargaModal._cargarDiasAsync] la ventana se cerró antes de que llegaran los días con envíos del GPS ${this._codigoInterno}: se descartan`);
        return;
      }
      if (dias.length === 0) {
        this._mostrarEstado("Este equipo todavía no ha enviado datos.", false);
        return;
      }
      this._pintarDias(dias);
    } catch (error) {
      console.warn(`[GpsTrackerDescargaModal._cargarDiasAsync] no se pudieron leer los días con envíos del GPS ${this._codigoInterno}`, error);
      if (this._cerrada) return;
      if (error instanceof NotFoundError) {
        this._mostrarEstado(error.message, false);
        return;
      }
      errorMessageComponent.addError("No se pudieron leer los días con datos", error instanceof Error ? error.message : "Error desconocido.");
      this._mostrarEstado("No se pudieron leer los días con datos.", true);
    }
  }

  /** Muestra un texto en vez de la lista y deja «Descargar» apagado: no hay días o falló la lectura. */
  private _mostrarEstado(texto: string, esError: boolean): void {
    const p = document.createElement("p");
    p.className = esError ? "gd-estado gd-estado--error" : "gd-estado";
    p.textContent = texto;
    this._cuerpo.replaceChildren(p);
    this._fecha = null;
    this._validar();
  }

  /**
   * Pinta los días con envíos como opciones de una sola elección, del más reciente al más viejo y con el primero ya
   * marcado. Escoger otro cambia el día que baja «Descargar».
   */
  private _pintarDias(dias: GpsTrackerDiaEnviosResponse[]): void {
    this._cuerpo.innerHTML = `
      <div class="cl-form-group">
        <span class="cl-form-label" id="gd-dias-titulo">Días con datos (${dias.length})</span>
        <div class="gd-dias" role="radiogroup" aria-labelledby="gd-dias-titulo">
          ${dias.map((d, i) => `
            <label class="gd-dia">
              <input type="radio" name="gd-dia" value="${d.fecha}"${i === 0 ? " checked" : ""} />
              <span class="gd-dia-fecha">${fmtDiaEnvios(d.fecha)}</span>
              <span class="gd-dia-envios">${d.envios} ${d.envios === 1 ? "envío" : "envíos"}</span>
            </label>`).join("")}
        </div>
        <span class="gt-form-note">Baja los envíos que llegaron ese día (hora de Colombia), tal cual los mandó el equipo. Si estuvo sin señal, pueden traer puntos de días anteriores.</span>
      </div>
    `;
    this._cuerpo.querySelector<HTMLDivElement>(".gd-dias")!.addEventListener("change", (e) => {
      this._fecha = (e.target as HTMLInputElement).value;
      this._validar();
    });
    this._fecha = dias[0].fecha;
    this._validar();
  }

  /**
   * «Descargar» solo con un día escogido, sin otra descarga en curso y con la ventana abierta. Mientras baja, el botón
   * dice «Descargando…» para que no parezca trabado: un día puede pesar varios MB.
   */
  private _validar(): void {
    this._descargarBtn.disabled = this._fecha === null || this._descargando || this._cerrada;
    this._descargarBtn.innerHTML = `<i class="ph ph-download-simple"></i> ${this._descargando ? "Descargando…" : "Descargar"}`;
  }

  /**
   * Pide los envíos del día escogido y los descarga como `gps_{código}_{fecha}.csv`. Si ese día llegó todo vacío, o ya
   * no hay envíos, lo avisa y deja la ventana abierta para escoger otro. Un error del servidor (GPS inexistente o que
   * nunca envió datos) se muestra con su texto y se escribe en consola. Cerrar la ventana mientras baja cancela la
   * descarga: la respuesta se descarta y queda anotado en consola.
   */
  private async _descargarAsync(): Promise<void> {
    const fecha = this._fecha;
    if (fecha === null || this._descargando || this._cerrada) return;
    this._descargando = true;
    this._validar();
    try {
      const r = await GpsTrackerService.instance.descargarEnvios(this._codigoInterno, fecha);
      if (this._cerrada) {
        console.warn(`[GpsTrackerDescargaModal._descargarAsync] la ventana se cerró antes de que llegaran los envíos del GPS ${this._codigoInterno} del ${fecha}: no se descarga`);
        return;
      }
      if (r.envios === 0) {
        errorMessageComponent.addWarning("Sin envíos ese día", `El GPS ${this._codigoInterno} no envió nada el ${fecha}.`);
        return;
      }
      if (r.contenido === "") {
        errorMessageComponent.addWarning("Envíos vacíos", `El ${fecha} llegaron ${r.envios} envíos del GPS ${this._codigoInterno}, todos vacíos.`);
        return;
      }
      downloadCsvTalCual(`gps_${this._codigoInterno}_${fecha}.csv`, r.contenido);
      this.dispose();
    } catch (error) {
      console.warn(`[GpsTrackerDescargaModal._descargarAsync] no se pudieron descargar los envíos del GPS ${this._codigoInterno} del ${fecha}`, error);
      if (!this._cerrada) errorMessageComponent.addError("No se pudo descargar", error instanceof Error ? error.message : "Error desconocido.");
    } finally {
      this._descargando = false;
      this._validar();
    }
  }
}
